import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  partPosition,
  unitCount,
  type ScopeUnit,
} from '@/lib/sources/scopeSelection';
import type { OutlineSection } from '@/types/api';

interface ScopePartRowProps {
  section: OutlineSection;
  unit: ScopeUnit;
  selected: boolean;
  /** Size of the largest part, so the size bars are comparable. */
  maxUnits: number;
  disabled?: boolean;
  onToggle: (id: string) => void;
}

/** One choosable part of a source: a chapter, a group of slides, a sheet. */
export function ScopePartRow({
  section: s,
  unit,
  selected,
  maxUnits,
  disabled = false,
  onToggle,
}: ScopePartRowProps) {
  const width = Math.round((s.est_units / Math.max(1, maxUnits)) * 100);
  return (
    <label
      className={cn(
        'grid cursor-pointer grid-cols-[18px_1fr_auto] items-center gap-3.5',
        'rounded-md border border-transparent px-3 py-2.5 transition-colors',
        selected ? 'border-primary/30 bg-primary/10' : 'hover:bg-accent/70',
        disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <input
        type='checkbox'
        checked={selected}
        disabled={disabled}
        onChange={() => onToggle(s.id)}
        className='peer sr-only'
      />
      <span
        className={cn(
          'flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border transition-colors',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1',
          selected
            ? 'border-primary bg-primary'
            : 'border-input bg-card dark:bg-transparent'
        )}
      >
        <Check
          className={cn(
            'h-3 w-3 text-primary-foreground',
            !selected && 'hidden'
          )}
          strokeWidth={3}
        />
      </span>

      <span className='min-w-0'>
        <span className='block truncate text-sm font-medium text-foreground'>
          {s.title}
        </span>
        <span className='mt-1.5 flex items-center gap-2.5'>
          <span className='relative h-1 flex-1 overflow-hidden rounded-full bg-secondary'>
            <span
              className={cn(
                'absolute inset-y-0 left-0 rounded-full',
                selected ? 'bg-primary' : 'bg-muted-foreground/50'
              )}
              style={{ width: `${width}%` }}
            />
          </span>
          <span className='whitespace-nowrap text-xs text-muted-foreground'>
            {unitCount(unit, s.est_units)}
          </span>
        </span>
      </span>

      <span className='whitespace-nowrap text-xs tabular-nums text-muted-foreground'>
        {partPosition(unit, s)}
      </span>
    </label>
  );
}
